import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { apiErr, fmtCredits } from "../../lib/api";
import { Card, SectionTitle, Alert, btnPrimary, btnSecondary, CostBadge } from "../ui-bits";
import { toast } from "sonner";
import { Wand2, CheckCircle2, Loader2, Circle, Clock, AlertTriangle, Film } from "lucide-react";
import { useQuota } from "../../context/QuotaContext";
import { useUploads } from "../../context/UploadContext";
import RenderHistory from "./RenderHistory";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../ui/alert-dialog";

const fmtElapsed = (sec) => {
  sec = Math.max(0, Math.round(sec || 0));
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

export default function TabRenderizar({ project, config, refreshProject, setTab }) {
  const { t } = useTranslation();
  const steps = config?.render_steps || [];
  const { quota, openUpgrade } = useQuota();
  const { startRender, getRender, isRendering, registerReload } = useUploads();
  const [starting, setStarting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // El estado del render vive en el contexto global (panel flotante). Aquí solo se lee.
  const globalRender = getRender ? getRender(project.id) : null;
  const render = globalRender?.raw || project?.render || { status: "idle", steps: [], log: [], progress: 0 };

  // Refresca el proyecto cuando el render global termina (usa el bus de recarga del contexto).
  useEffect(() => registerReload && registerReload(project.id, refreshProject), [project.id, refreshProject, registerReload]);

  // Si al abrir la pestaña el proyecto ya está renderizando (p. ej. recarga, cambio de pestaña,
  // o el render lo acaba de lanzar la producción automática), reanuda el sondeo global.
  useEffect(() => {
    if ((project?.render?.status === "rendering" || project?.render?.status === "en_cola") && !isRendering(project.id)) {
      startRender(project.id, project?.title, { resume: true });
    }
  }, [project?.id, project?.render?.status]); // eslint-disable-line

  // Producción automática en curso (narración + B-roll antes del render): sondear el proyecto
  // para reflejar el paso actual y detectar cuándo arranca el render de verdad.
  const auto = project?.auto_produce;
  const autoActive = auto?.status === "en_curso";
  const autoError = auto?.status === "error" ? auto?.error : null;
  useEffect(() => {
    if (!autoActive || !refreshProject) return;
    const id = setInterval(() => refreshProject(), 5000);
    return () => clearInterval(id);
  }, [autoActive, refreshProject]);

  const segments = project?.segments || [];
  const clips = project?.broll?.clips || [];
  const sceneAudio = (s) => s.narration_duration || s.duration || 0;
  const sceneComplete = (s) => {
    if (s.narration_status !== "completo") return false;
    const au = sceneAudio(s);
    if (au <= 0) return false;
    const covered = clips.filter((c) => c.segment_id === s.id).reduce((x, c) => x + (c.duration || 0), 0);
    // Tolerancia 3.5s (coincide con GRAPHIC_FILL_MIN_GAP del backend, no 0.5s): un hueco menor
    // lo absorbe el freeze-frame del render — exigir casi cero margen bloqueaba escenas
    // posteriores perfectamente completas por un déficit mínimo en una sola escena.
    return covered >= au - 3.5;
  };
  const readyScenes = [];
  for (const s of segments) { if (sceneComplete(s)) readyScenes.push(s); else break; }
  const estSeconds = Math.round(readyScenes.reduce((a, s) => a + sceneAudio(s), 0));
  const remaining = quota?.seconds_remaining ?? null;
  const enough = remaining === null || remaining >= estSeconds;

  const start = async () => {
    setConfirmOpen(false);
    setStarting(true);
    try {
      await startRender(project.id, project?.title);
    } catch (e) { toast.error(apiErr(e)); } finally { setStarting(false); }
  };

  const askConfirm = () => setConfirmOpen(true);

  // "rendering"/"en_cola" ya no se pueden fundir en un solo booleano: la UI de cada uno es
  // distinta (barra de progreso + pasos vs. simplemente "estás en la posición N"). `busy` es
  // el booleano amplio ("ocupado, sea como sea") para las condiciones que solo necesitan
  // ocultar el botón de crear — `isRendering(project.id)` cubre además el instante optimista
  // antes de que llegue la primera respuesta de /render/status (ver UploadContext.startRender).
  const rendering = render.status === "rendering";
  const queued = render.status === "en_cola";
  const busy = rendering || queued || isRendering(project.id);
  const done = render.status === "completo";
  const canRender = readyScenes.length >= 2;
  const partial = canRender && readyScenes.length < segments.length;

  return (
    <div className="space-y-6">
      <SectionTitle sub={t("render.subtitle")}>{t("render.title")}</SectionTitle>

      {autoError && !busy && !done && (
        <Alert type="error" testid="auto-produce-error">{autoError}</Alert>
      )}

      {autoActive && (
        <Card className="p-6 border-2 border-blue-200 bg-blue-50/40" data-testid="auto-produce-progress">
          <div className="flex items-center gap-3">
            <Loader2 className="animate-spin text-blue-600 shrink-0" size={28} />
            <div>
              <div className="text-lg font-bold text-slate-900">{t("render.autoTitle")}</div>
              <div className="text-base text-slate-600">
                {auto?.step === "broll" ? t("render.autoBroll") : t("render.autoNarr")}
                {auto?.step_detail ? ` · ${auto.step_detail}` : ""}
              </div>
              <div className="text-sm text-slate-500 mt-1">{t("render.autoHint")}</div>
            </div>
          </div>
        </Card>
      )}

      {!canRender && !autoActive && <Alert type="warning" testid="render-not-ready">{t("render.notReady")}</Alert>}

      {partial && !busy && !done && !autoActive && (
        <Alert type="info" testid="render-partial-note">
          {t("render.partial", { ready: readyScenes.length, total: segments.length, next: readyScenes.length + 1 })}
        </Alert>
      )}

      {!busy && !done && !autoActive && (
        <div className="flex flex-col items-center gap-3">
          <button data-testid="generate-video-button" onClick={askConfirm} disabled={starting || !canRender} className="min-h-[48px] px-4 sm:px-5 text-base font-bold rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
            {starting ? <Loader2 className="animate-spin" size={20} /> : <Wand2 size={20} />} {t("render.createBtn")}
            {canRender && estSeconds > 0 && (
              <CostBadge seconds={estSeconds} tip={t("render.costTip", { n: readyScenes.length })} />
            )}
          </button>
          {/* Sin duración: el coste ya lo dice la chapa del botón EN CRÉDITOS, y ponerlo además
              en mm:ss era el mismo número en la unidad vieja (34:39 = 2.079 créditos, porque un
              crédito nació siendo un segundo de vídeo). Ver EUR_POR_CREDITO. */}
          {canRender && estSeconds > 0 && (
            <p data-testid="render-cost-hint" className="text-base text-slate-500 flex items-center gap-1.5">
              <Trans i18nKey="render.costHint" values={{ n: readyScenes.length }} components={{ 0: <b className="text-slate-700" /> }} />
            </p>
          )}
        </div>
      )}

      {queued && (
        <Card className="p-8 sm:p-10" data-testid="render-queued-card">
          <div className="flex flex-col items-center text-center max-w-md mx-auto">
            <div className="relative mb-5 w-24 h-24 flex items-center justify-center shrink-0">
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-blue-200 animate-spin" style={{ animationDuration: "12s" }} />
              <div className="w-14 h-10 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-blue-200 flex items-center justify-center shadow-sm">
                <Film size={20} className="text-blue-500" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-1.5" data-testid="render-queued-title">{t("render.queuedTitle")}</h3>
            <p className="text-base text-slate-500" data-testid="render-queued-note">{t("render.queuedNote")}</p>
            <div className="w-full border-t border-slate-100 mt-5 pt-4">
              <p className="text-sm text-slate-400">{t("render.queuedFooter")}</p>
            </div>
          </div>
        </Card>
      )}

      {(rendering || done) && (
        <Card className="p-6">
          {rendering && (
            <Alert type="info" testid="render-background-note">
              <Trans i18nKey="render.bgNote" components={{ 0: <b /> }} />
            </Alert>
          )}
          <div className="flex items-center justify-between mb-1 mt-4">
            <span className="text-lg font-bold text-slate-900">{t("render.overallProgress")}</span>
            <span data-testid="render-progress" className="text-2xl font-bold font-head text-blue-600">{render.progress || 0}%</span>
          </div>
          {rendering && render.elapsed_seconds != null && (
            <div data-testid="render-elapsed" className="flex items-center gap-1.5 text-sm text-slate-500 mb-2">
              <Clock size={14} /> {t("render.elapsed", { time: fmtElapsed(render.elapsed_seconds) })}
            </div>
          )}
          <div className="w-full h-8 bg-slate-200 rounded-full overflow-hidden mb-4">
            <div className="h-full bg-blue-600 rounded-full transition-all duration-500" style={{ width: `${render.progress || 0}%` }} />
          </div>

          {rendering && render.detail_items?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-6" data-testid="render-activity">
              {render.detail_items.map((item, i) => {
                const isGlobal = item.key.startsWith("_global");
                return (
                  <div key={item.key} style={{ animationDelay: `${i * 60}ms` }}
                    className={`animate-in fade-in slide-in-from-bottom-1 duration-300 flex items-start gap-3 rounded-xl border pl-3 pr-3.5 py-2.5 ${isGlobal ? "border-indigo-200 bg-indigo-50/70" : "border-blue-200 bg-blue-50/70"}`}>
                    <span className="relative flex h-2.5 w-2.5 shrink-0 mt-1.5">
                      <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${isGlobal ? "bg-indigo-500" : "bg-blue-500"}`} />
                      <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${isGlobal ? "bg-indigo-600" : "bg-blue-600"}`} />
                    </span>
                    <div className="min-w-0">
                      <div className={`text-[11px] font-bold uppercase tracking-wider ${isGlobal ? "text-indigo-500" : "text-blue-500"}`}>
                        {isGlobal ? t("render.globalStep") : t("render.sceneLabel", { num: item.key })}
                      </div>
                      <div className="text-sm font-semibold text-slate-800 leading-snug">{item.text}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="space-y-2 mb-6" data-testid="render-steps">
            {(render.steps?.length ? render.steps : steps.map((n) => ({ name: n, status: "pendiente" }))).map((s, i) => (
              <div key={i} className="flex items-center gap-3 text-lg">
                {s.status === "completo" ? <CheckCircle2 size={28} className="text-emerald-600" />
                  : s.status === "en_proceso" ? <Loader2 size={28} className="text-blue-600 animate-spin" />
                  : <Circle size={28} className="text-slate-300" />}
                <span className={`font-medium ${s.status === "completo" ? "text-slate-900" : s.status === "en_proceso" ? "text-blue-700 font-bold" : "text-slate-400"}`}>{i + 1}. {s.name}</span>
              </div>
            ))}
          </div>

          {done && (
            <div className="mt-4 space-y-3">
              <Alert type="success" testid="render-done">{t("render.doneAlert")}</Alert>
              <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-4 text-base text-slate-700">
                {t("render.changedNote")}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button data-testid="rerender-button" onClick={askConfirm} disabled={starting || !canRender} className={`flex-1 ${btnSecondary} disabled:opacity-50`}>
                  {starting ? <Loader2 className="animate-spin" size={22} /> : <Wand2 size={22} />} {t("render.rerender")}
                  {canRender && estSeconds > 0 && (
                    <CostBadge dark seconds={estSeconds} tip={t("render.rerenderTip", { n: readyScenes.length })} />
                  )}
                </button>
                <button data-testid="go-download-button" onClick={() => setTab("miniatura")} className={`flex-1 ${btnPrimary}`}>{t("render.goThumbnail")}</button>
              </div>
            </div>
          )}
        </Card>
      )}

      <RenderHistory project={project} refreshProject={refreshProject} />

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="render-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl flex items-center gap-2">
              <Clock className="text-blue-600" size={26} /> {t("render.confirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-lg text-slate-700 pt-2 space-y-3">
              <span className="block">
                <Trans i18nKey="render.confirmBody" values={{ n: readyScenes.length, time: fmtCredits(estSeconds) }} components={{ 0: <b className="text-slate-900" />, 1: <b data-testid="render-cost" className="text-slate-900" /> }} />
              </span>
              {remaining !== null && (
                <span className={`block ${enough ? "text-slate-600" : "text-red-700 font-semibold"}`}>
                  {t("render.remainingLine", { time: fmtCredits(remaining), plan: quota?.plan_label ? ` (${quota.plan_label}).` : "." })}
                </span>
              )}
              {!enough && (
                <span className="flex items-start gap-2 bg-red-50 border-2 border-red-200 text-red-700 rounded-xl px-4 py-3 text-base">
                  <AlertTriangle size={20} className="shrink-0 mt-0.5" />
                  {t("render.notEnough")}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="render-confirm-cancel" className="min-h-[48px] text-base">{t("render.cancel")}</AlertDialogCancel>
            {enough ? (
              <AlertDialogAction data-testid="render-confirm-accept" onClick={start} className="min-h-[48px] text-base bg-blue-600 hover:bg-blue-700">
                {t("render.confirmAccept")}
              </AlertDialogAction>
            ) : (
              <AlertDialogAction data-testid="render-confirm-upgrade" onClick={() => { setConfirmOpen(false); openUpgrade(); }} className="min-h-[48px] text-base bg-blue-600 hover:bg-blue-700">
                {t("render.confirmUpgrade")}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
